import { useState, useEffect, useReducer, Reducer, useCallback, useMemo } from "react"
import { AdditionalParams, Result, Data } from "@shared/platform-api/requests"
import { useDeepCompareEffect, useDebounce } from "react-use"
import {
  makePaginatedApiReducer,
  PaginatedApiState,
  PaginatedApiAction,
  makeInitialState,
  findFeSortKey,
} from "./paginatedApiReducer"
import {
  TableColumnSortOrder,
  UsePaginatedRequestProps,
  UsePaginatedRequestState,
  PaginatedRequestTypes,
} from "../types"

export const usePaginatedApi = <T, I, SK, FESK>(
  input: I & AdditionalParams<SK>,
  reload: (newInput?: I & AdditionalParams<SK>) => void,
  result?: Result<Data<T>>,
  sortKeyMap?: Map<FESK, SK>,
  queryStringAndStateSetters?: Record<string, (newValue: string) => void>
) => {
  const paginatedApiReducer = makePaginatedApiReducer<T, FESK>()
  const [requestInput, setRequestInput] = useState<I & AdditionalParams<SK>>(input)
  const [searchTerm, setSearchTerm] = useState(input.term || ``)
  const [state, dispatch] = useReducer<Reducer<PaginatedApiState<T, FESK>, PaginatedApiAction<T, FESK>>>(
    paginatedApiReducer,
    makeInitialState<T, I, SK, FESK>(input, sortKeyMap)
  )

  useDeepCompareEffect(() => {
    reload(requestInput)
  }, [requestInput])

  useEffect(() => {
    if (result?.tag !== `ok`) return
    if (requestInput?.after) {
      dispatch({ type: `addApiPage`, value: result.data })
      return
    }
    dispatch({ type: `refresh`, data: result.data, pageSize: requestInput.limit || 10 })
  }, [result])

  const onNextPage = () => {
    const { pageIndex, pages } = state
    if (pageIndex + 1 >= pages.length) {
      setRequestInput({ ...requestInput, after: state.next || undefined } as I & AdditionalParams<SK>)
      return
    }
    dispatch({ type: `forwards` })
  }

  const onPrevPage = () => {
    dispatch({ type: `backwards` })
  }

  const changePageSize = (pageSize: number) => {
    if (queryStringAndStateSetters) queryStringAndStateSetters.setLimit(pageSize.toString())
    setRequestInput({ ...requestInput, after: undefined, limit: pageSize } as I & AdditionalParams<SK>)
  }

  useDebounce(
    () => {
      if (searchTerm !== requestInput.term) {
        if (queryStringAndStateSetters) {
          queryStringAndStateSetters.setSearchTerm(searchTerm || ``)
        }
        setRequestInput(
          (prev) =>
            ({
              ...prev,
              after: undefined,
              term: searchTerm,
            } as I & AdditionalParams<SK>)
        )
      }
    },
    450,
    [searchTerm]
  )

  const setSort = (feSortKey: FESK, sortOrder: TableColumnSortOrder | undefined) => {
    if (!sortKeyMap) return
    const newSortKey = sortKeyMap.get(feSortKey)
    if (!newSortKey) return
    if (queryStringAndStateSetters) {
      queryStringAndStateSetters.setSortOrder(sortOrder || ``)
      queryStringAndStateSetters.setSortKey((sortOrder ? feSortKey : ``) as string)
    }
    dispatch({ type: `setSortProperty`, value: feSortKey })
    setRequestInput({ ...requestInput, after: undefined, sortKey: newSortKey, sortOrder } as I & AdditionalParams<SK>)
  }

  const changeRequestInput = (newRequestInput: I & AdditionalParams<SK>) => {
    setRequestInput({
      ...newRequestInput,
      after: undefined,
    })
  }

  return useMemo(
    () => ({
      page: state.currentPage || [],
      dataCount: state.dataCount,
      search: {
        value: searchTerm,
        onChange: setSearchTerm,
        debounceDelay: 450,
      },
      sort: {
        property: state.sortProperty,
        order: requestInput?.sortOrder,
      },
      setSort,
      mutateRequestInput: {
        mutate: changeRequestInput,
        currentRequestInput: requestInput,
      },
      pagination: {
        pageIndex: state.pageIndex,
        pageSize: requestInput?.limit || input.limit || 10,
        pageCount: state.pageCount,
        prevDisabled: state.prevDisabled,
        nextDisabled: state.nextDisabled,
        onNextPage,
        onPrevPage,
        changePageSize,
      },
    }),
    [state, searchTerm, requestInput, input.limit]
  )
}

const makeInitialPaginatedRequestState = <I, O, SK, FESK>(props: UsePaginatedRequestProps<I, O, SK, FESK>) => ({
  loading: true,
  page: [],
  pages: [],
  pageSize: 0,
  pageCount: 0,
  pageIndex: 0,
  total: 0,
  prev: null,
  next: null,
  requestInput: props.input,
  prevDisabled: true,
  nextDisabled: true,
  error: undefined,
  sortKey: findFeSortKey(props.input.sortKey, props.sortKeyMap),
})

export const usePaginatedRequest = <I, O, SK = [], FESK = []>(props: UsePaginatedRequestProps<I, O, SK, FESK>) => {
  const [state, setState] = useState<UsePaginatedRequestState<I, O, SK, FESK>>(
    makeInitialPaginatedRequestState<I, O, SK, FESK>(props)
  )
  const [searchTerm, setSearchTerm] = useState(props.input.term || ``)

  /** Function to trigger the API and handle updating the state
   *  If the requested page already exists it will be loaded from the state.
   */
  const makeRequest = useCallback(
    async (
      reqType: PaginatedRequestTypes,
      index: number,
      pages: ReadonlyArray<{ requestInput: I & AdditionalParams<SK>; page: ReadonlyArray<O> }>,
      reqState?: UsePaginatedRequestState<I, O, SK, FESK> /* I & AdditionalParams<SK> */,
      forceReq = false
    ) => {
      // no negative pages allowed
      if (index < 0) return

      const isCached = reqState !== undefined && (index <= reqState.pageIndex || index + 1 <= reqState?.pages.length)
      const canLoadFromCache = reqType === `NEXT` || reqType === `PREV`
      // if we already have the page stored, use it and save a network request
      if (!forceReq && canLoadFromCache && isCached) {
        const { page, requestInput } = reqState.pages[index]
        const pageIndex = index

        setState({
          ...reqState,
          page,
          pageIndex,
          prevDisabled: index === 0,
          nextDisabled: index + 1 >= reqState.pageCount,
          requestInput,
        })

        return
      }

      try {
        const requestInput = reqState?.requestInput ?? props.input
        const response = await props.request(requestInput ?? props.input)
        const page = response?.items ?? ([] as Readonly<Array<O>>)
        const total = response?.total ?? 0
        const pageSize = reqState?.requestInput?.limit ?? props?.input.limit ?? 10
        const pageCount = total === pageSize ? 1 : Math.ceil(total / pageSize)

        if (total !== 0 && index + 1 > pageCount) return

        const isViewingAll = pageSize === total
        const prev = reqState?.next ?? null
        const next = response?.next ?? null
        const pageIndex = index
        const prevDisabled = index === 0
        const nextDisabled = isViewingAll || next === null
        const cachedPages = isViewingAll ? [{ page, requestInput }] : [...pages, { requestInput, page }]

        setState({
          loading: false,
          page,
          pages: cachedPages,
          total,
          pageSize,
          pageCount,
          prev,
          next,
          requestInput: reqState?.requestInput ?? props.input,
          pageIndex,
          prevDisabled,
          nextDisabled,
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Error making usePaginated request: ${props.request.toString()}`)

        // relay the error to the HOC, it can then display an API error page, or throw..
        setState({
          ...makeInitialPaginatedRequestState<I, O, SK, FESK>(props),
          error,
        })
      }
    },
    [props]
  )

  /** recalls the existing request - useful to trigger a rerender when something has changed in the server
   * Eg form submission
   */
  const refresh = useCallback(
    async () => makeRequest(`REFRESH`, state.pageIndex, state.pages, state),
    [makeRequest, state]
  )

  // Initial call, so no deps are needed.
  useEffect(() => {
    ;(async () => makeRequest(`INITIAL`, 0, []))()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Function to load the next page.
   */
  const onNextPage = useCallback(async () => {
    const pageIndex = state.pageIndex + 1
    await makeRequest(`NEXT`, pageIndex, state.pages, {
      ...state,
      requestInput: { ...state.requestInput, after: state.next ?? undefined },
    })
  }, [makeRequest, state /** state.requestInput, state.next, state.pageIndex, state.pages**/])

  /**
   * Function to load the previous page.
   */
  const onPrevPage = useCallback(async () => {
    const pageIndex = state.pageIndex - 1
    if (pageIndex < 0) return
    await makeRequest(`PREV`, pageIndex, state.pages, state)
  }, [makeRequest, state])

  /**
   * Function to make a network request with an input of your choosing
   * This function is returned so it can be used in a Higher order component for example.
   * The pageIndex will always reset back to 0 when making this request
   */
  const mutate = useCallback(
    (requestInput: I & AdditionalParams<SK>) => makeRequest(`MUTATE`, 0, [], { ...state, requestInput }, true),
    [makeRequest, state]
  )

  /**
   * Function to change the page size (or the limit) of a network request
   */
  const changePageSize = useCallback(
    async (pageSize: number) => mutate({ ...state.requestInput, limit: pageSize }),

    [mutate, state.requestInput /** state.requestInput*/]
  )

  useDebounce(
    () => {
      mutate({ ...state.requestInput, term: searchTerm })
    },
    450,
    [searchTerm]
  )

  // @TODO another time.
  // const setSort = useCallback(
  //   async (feSortKey: FESK, sortOrder: TableColumnSortOrder | undefined) => {
  //     if (!props.sortKeyMap) return
  //     const newSortKey = props.sortKeyMap.get(feSortKey)
  //     if (!newSortKey) return
  //     await mutate({ ...state.requestInput, after: undefined, sortKey: newSortKey, sortOrder })
  //   },
  //   [mutate, state.requestInput, props.sortKeyMap]
  // )

  return useMemo(
    () => ({
      loading: state.loading,
      page: state.page || [],
      dataCount: state.total ?? 0,
      search: {
        value: searchTerm,
        onChange: setSearchTerm,
        debounceDelay: 450,
      },
      // @TODO another time.
      // sort: {
      //   property: state.sortProperty,
      //   order: state.requestInput?.sortOrder,
      // },
      // setSort,
      mutateRequestInput: {
        mutate,
        currentRequestInput: state.requestInput,
      },
      pagination: {
        pageIndex: state.pageIndex,
        pageSize: state.requestInput?.limit || props.input.limit || 10,
        pageCount: state.pageCount,
        prevDisabled: state.prevDisabled,
        nextDisabled: state.nextDisabled,
        onNextPage,
        onPrevPage,
        changePageSize,
      },
      error: state.error,
      refresh,
    }),
    [state, searchTerm, mutate, changePageSize, onNextPage, onPrevPage, props.input.limit, refresh]
  )
}
